import { format } from 'date-fns';
import type { StatusType, StatusRow } from '../components/Dialogs/Statuses/StatusesTable.types';
import {
  DAY_NAMES,
  CUSTOMS_CLEARANCE_STATUSES,
  TRIANGULATION_STATUSES,
  WEIGHING_STATUSES,
} from '../TransportCloudTable.consts';
import { Item, GroupedItems } from '../TransportCloudTable.types';

const DISPLAY_DATE_FORMAT = 'dd-MM-yyyy';

export const getTranslatedWeekday = (date: Date): string => {
  const weekdays = [
    DAY_NAMES.sunday,
    DAY_NAMES.monday,
    DAY_NAMES.tuesday,
    DAY_NAMES.wednesday,
    DAY_NAMES.thursday,
    DAY_NAMES.friday,
    DAY_NAMES.saturday,
  ];
  return weekdays[date.getDay()] || '';
};

export const mapStatusValue = (status: string, tradeType: string): string => {
  if (!tradeType) return '';

  const mappings: Record<string, Record<string, string>> = {
    IMPORT: {
      DONE: CUSTOMS_CLEARANCE_STATUSES.import.done,
      IN_PROGRESS: CUSTOMS_CLEARANCE_STATUSES.import.inProgress,
      NONE: CUSTOMS_CLEARANCE_STATUSES.none,
    },
    EXPORT: {
      DONE: CUSTOMS_CLEARANCE_STATUSES.export.done,
      IN_PROGRESS: CUSTOMS_CLEARANCE_STATUSES.export.inProgress,
      NONE: CUSTOMS_CLEARANCE_STATUSES.none,
    },
  };

  return mappings[tradeType]?.[status] || '';
};

export const mapTriangulationValue = (triangulation: string): string => {
  const mappings: Record<string, string> = {
    APPROVED: TRIANGULATION_STATUSES.approved,
    IN_APPROVAL: TRIANGULATION_STATUSES.inApproval,
    DEPOT: TRIANGULATION_STATUSES.depot,
  };

  return mappings[triangulation] || '';
};

export const mapWeighingValue = (weighing: string): string => {
  const mappings: Record<string, string> = {
    AT_LOADING: WEIGHING_STATUSES.atLoading,
    AT_PORT: WEIGHING_STATUSES.atPort,
    OTHER_ADDRESS: WEIGHING_STATUSES.otherAddress,
  };

  return mappings[weighing] || '';
};

export const formatRate = (rate?: number, currency?: string) => {
  const rateValue = rate !== undefined && rate !== null ? rate : '';
  return `${rateValue}${currency ? ` ${currency}` : ''}`.trim();
};

export const attachContainerStatus = (item: any): { containerStatus: StatusRow | null } & Record<string, any> => ({
  ...item,
  containerStatus: item.statusContainerId
    ? {
        id: item.statusContainerId,
        kind: (item.tradeType === 'EXPORT' ? 'EXPORT' : 'IMPORT') as StatusType,
        name: item.statusContainer ?? '',
        color: item.statusContainerColor ?? null,
      }
    : null,
});

export const enrichItem = (item: any): Item => ({
  ...item,
  lp: item.lp,
  shippingLine: item.shippingLineCode || '',
  containerType: item.containerType || '',
  customer: item.customerCode || '',
  job: item.jobCode || '',
  referenceNumbers: item.combinedReferenceNumbers || '',
  bookingNumber: item.bookingNumber || '',
  pinCode: item.pinCode || '',
  tradeType: item.tradeType || '',
  pickupLocation: item.pickupLocationDescription || '',
  pickupDate: item.pickupDate || '',
  loadingDeliveryLocation: item.loadingDeliveryLocationAddress || '',
  loadingDeliveryDate: item.loadingDeliveryDate || '',
  deliveryLocation: item.deliveryLocationDescription || '',
  deliveryDate: item.deliveryDate || '',
  customsClearance: mapStatusValue(
    item.customsClearance || '',
    item.tradeType || '',
  ),
  weighing: mapWeighingValue(item.weighing || ''),
  productGroup: item.productGroup || '',
  loadDescription: item.loadDescription || '',
  comment: item.comment || '',
  documents: '',
  offerTruckerRate: formatRate(
    item.offerTruckerRate,
    item.offerTruckerCurrency,
  ),
  truckerRate: formatRate(item.truckerRate, item.truckerRateCurrency),
  containerNumber: item.containerNumber || '',
  triangulation: mapTriangulationValue(item.triangulation || ''),
  containerStatus: item.containerStatus ?? null,
  trucker: item.truckerCode || '',
  driverName: item.driverName || '',
  driverDocumentNumber: item.driverDocumentNumber || '',
  driverPhone: item.driverPhone || '',
  vehicleRegistrationNumber: item.vehicleRegistrationNumber || '',
  trailerRegistrationNumber: item.trailerRegistrationNumber || '',
  reportNotes: item.reportNotes || '',
  actions: '',
  shippingLineHaulage: item.shippingLineHaulage || false,
  captured: item.captured || false,
});

export const parseItemDate = (dateString: string) => {
  try {
    const isDateFormat =
      dateString.includes('-') && dateString.split('-').length === 3;
    if (isDateFormat) {
      const [day, month, year] = dateString.split('-');
      return new Date(`${year}-${month}-${day}`);
    }
    return new Date(dateString);
  } catch {
    return null;
  }
};

export const groupItemsByDate = (items: Item[]): GroupedItems[] => {
  const grouped = items.reduce((acc, item) => {
    if (!item.loadingDeliveryDate) {
      const noDateKey = 'no-loading-delivery-date';
      (acc[noDateKey] ??= []).push(item);
      return acc;
    }

    const dateKey = item.loadingDeliveryDate.includes(' ')
      ? item.loadingDeliveryDate.split(' ')[0]
      : item.loadingDeliveryDate;

    (acc[dateKey] ??= []).push(item);
    return acc;
  }, {} as Record<string, Item[]>);

  return Object.entries(grouped).map(([date, dateItems]) => {
    const jobCodeGroups: { jobCode: string | null; items: Item[] }[] = [];

    dateItems.forEach((item) => {
      const currentJobCode = item.jobCode || null;
      const lastGroup = jobCodeGroups[jobCodeGroups.length - 1];

      if (lastGroup && lastGroup.jobCode === currentJobCode) {
        lastGroup.items.push(item);
      } else {
        jobCodeGroups.push({
          jobCode: currentJobCode,
          items: [item],
        });
      }
    });

    if (date === 'no-loading-delivery-date') {
      return {
        date: DAY_NAMES.noLoadingDeliveryDate,
        weekday: '',
        jobCodeGroups,
      };
    }

    const parsedDate = parseItemDate(date);
    if (parsedDate) {
      return {
        date: format(parsedDate, DISPLAY_DATE_FORMAT),
        weekday: getTranslatedWeekday(parsedDate),
        jobCodeGroups,
      };
    }

    return { date, weekday: '', jobCodeGroups };
  });
};
